import {HTTPClient as HTTPClientCore, IRequestConfig} from "modules/utils/HTTPClient";
import axios, {AxiosError} from "axios";
import {isObject, set} from "lodash";
import ApiError from "./ApiError";
import {API_URL, HELP_JSON_URL, JSON_URL} from "modules/constants";
import {
	IApiResponse,
	IArticles,
	ICountry,
	IFanVoteRanking,
	IFetchTeamsResponse,
	IFetchVotesResponse,
	IHelpArticle,
	IHelpCategory,
	IHelpSection,
	IPlayer,
	IProfileResponse,
	ISaveProfilePayload,
	ISaveTeamPayload,
	ISaveVotePayload,
	ISettings,
	IShareImageResponse,
	ISquad,
	IUserResponse,
	IWithLanguage,
} from "modules/types";
import {CANCEL} from "redux-saga";
import {getHelpJsonPath, getLang, withTimeStamp} from "modules/utils/helpers";

class HTTPClient extends HTTPClientCore {
	/**
	 * Overridden method adds CancelToken symbol, that allow redux-saga'
	 * "takeLatest" function to cancel any requests automatically.
	 * http://fe-common-utils.s3-website-eu-west-1.amazonaws.com/classes/httpclient.html
	 */
	public makeRequest<T>(config: IRequestConfig): Promise<T> {
		const source = axios.CancelToken.source();

		const request = super.makeRequest<T>({
			...config,
			cancelToken: source.token,
		});

		return set<Promise<T>>(request, CANCEL, () => source.cancel());
	}
}

const onCatchNetworkError = async ({
	response,
	message = "Network error",
}: AxiosError<IApiResponse>) => {
	const data = response?.data;
	const error = isObject(data)
		? {
				...data,
				errors: data.errors.map((e) => new ApiError(e.message, response?.status)),
		  }
		: {
				errors: [new ApiError(message, response?.status)],
		  };

	try {
		return await Promise.reject(error);
	} catch (err) {
		return ApiError.CHECK(err as IApiResponse);
	}
};

export const APIClient = new HTTPClient({
	baseURL: API_URL,
	withCredentials: true,
	onCatchNetworkError,
});

export const JSONClient = new HTTPClient({
	baseURL: JSON_URL,
	onCatchNetworkError,
});

const HelpJSONClient = new HTTPClient({
	baseURL: HELP_JSON_URL,
	onCatchNetworkError,
});

export const Api = {
	JSON: {
		checksums: () => JSONClient.get(withTimeStamp("checksum.json")),
		players: () => JSONClient.get<IPlayer[]>(withTimeStamp("players.json")),
		squads: () => JSONClient.get<ISquad[]>(withTimeStamp("squads.json")),
		settings: () => JSONClient.get<ISettings>(withTimeStamp("settings.json")),
		countries: () => JSONClient.get<ICountry[]>(withTimeStamp("countries.json")),
		fanVoteRankings: () =>
			JSONClient.get<IFanVoteRanking[]>(withTimeStamp("fan_favorite_rankings.json")),
		articles: () => JSONClient.get<IArticles>(withTimeStamp("articles.json")),
	},
	Help: {
		categories: () => HelpJSONClient.get<IHelpCategory>(`${getHelpJsonPath()}categories.json`),
		sections: (categoryId: number) =>
			HelpJSONClient.get<IHelpSection>(
				`${getHelpJsonPath()}categories/${categoryId}/sections.json`
			),
		articles: (categoryId: number) =>
			HelpJSONClient.get<IHelpArticle>(
				`${getHelpJsonPath()}categories/${categoryId}/articles.json`
			),
	},
	Team: {
		get: () => APIClient.get<IApiResponse<IFetchTeamsResponse>>(`${getLang()}/votes`),
		getHistory: () =>
			APIClient.get<IApiResponse<IFetchTeamsResponse>>(`${getLang()}/votes/history`),
		save: (data: ISaveTeamPayload) =>
			APIClient.post<IApiResponse<IFetchTeamsResponse>>(`${getLang()}/votes`, data),
		getShareImage: (data: IWithLanguage<number>) =>
			APIClient.get<IApiResponse<IShareImageResponse>>(
				`${data.language}/sharing-image/${data.payload}`
			),
	},
	User: {
		get: () => APIClient.get<IApiResponse<IUserResponse>>(`${getLang()}/user`),
	},
	Profile: {
		get: () => APIClient.get<IApiResponse<IProfileResponse>>(`${getLang()}/profile`),
		save: (params: ISaveProfilePayload) =>
			APIClient.post<IApiResponse<IProfileResponse>>(`${getLang()}/profile/save`, params),
	},
	Vote: {
		getHistory: () =>
			APIClient.get<IApiResponse<IFetchVotesResponse>>(`${getLang()}/fan-favorite/history`),
		save: (params: ISaveVotePayload) =>
			APIClient.post<IApiResponse<IFetchVotesResponse>>(`${getLang()}/fan-favorite`, params),
		getShareImage: (data: IWithLanguage<number>) =>
			APIClient.get<IApiResponse<IShareImageResponse>>(
				`${data.language}/sharing-image/fan-favorite/${data.payload}`
			),
	},
};

export * from "./ApiError";

export default Api;
